import { AxiosInstance } from 'axios';
import { Stage } from './issues';

/** One issue as an example in the report. Mirrors ReevaluationExampleDto. */
export interface ReevaluationExample {
    customer: string;
    environment: string;
    objectType: string;
    relationId: string;
    reason?: string | null;
}

/** A rule or section with its count and a few examples. Mirrors ReevaluationRuleDto. */
export interface ReevaluationRule {
    rule: string;
    label: string;
    count: number;
    examples: ReevaluationExample[];
}

/** Issues that go from one kind of problem to another. Mirrors ReevaluationRenameDto. */
export interface ReevaluationRename {
    from?: string | null;
    to: string;
    title?: string | null;
    nowRecognised: boolean;
    /** The kind of problem stays the same: only its title or owner is brought up to date. */
    textOnly: boolean;
    count: number;
    examples: ReevaluationExample[];
}

/** What stays open of one kind of problem. Mirrors ReevaluationCodeDto. */
export interface ReevaluationCode {
    code: string;
    title?: string | null;
    owner?: string | null;
    action?: string | null;
    hasChecker: boolean;
    count: number;
    unknown: number;
}

/** What the dry run found. Mirrors ReevaluationReportDto. */
export interface ReevaluationReport {
    examined: number;
    settled: number;
    toClose: number;
    keepOpen: number;
    unknown: number;
    /** Both kinds of reclassification together: `toRecode` and `toRetext`. */
    toReclassify: number;
    /** Reclassifications to another kind of problem. */
    toRecode: number;
    /** Reclassifications that only bring the title or owner up to date. */
    toRetext: number;
    toReopen: number;
    nowRecognised: number;
    /** Closed or resolved issues that stay as they are; with the other counts it adds up to `examined`. */
    unchanged: number;
    rules: ReevaluationRule[];
    reclassified: ReevaluationRename[];
    reopen: ReevaluationRule;
    personSet: ReevaluationRule;
    undecided: ReevaluationRule;
    open: ReevaluationCode[];
    withoutChecker: ReevaluationCode[];
}

/** How applying went. Mirrors ReevaluationApplyDto. */
export interface ReevaluationApply {
    appliedBy?: string | null;
    appliedAt?: string | null;
    complete: boolean;
    closed: number;
    reopened: number;
    reclassified: number;
    skippedChanged: number;
    skippedNotOpen: number;
    skippedMissing: number;
    skippedPersonSet: number;
}

export type ReevaluationState = 'Running' | 'Done' | 'Applying' | 'Applied';

/** One re-evaluation. Mirrors ReevaluationRunDto. */
export interface ReevaluationRun {
    runId: string;
    createdBy?: string | null;
    createdAt: string;
    scope: string;
    state: ReevaluationState;
    complete: boolean;
    environments: number;
    environmentsDone: number;
    current?: string | null;
    steps: number;
    lastStepAt?: string | null;
    stepNote?: string | null;
    exactCalls: number;
    chargebeeCalls: number;
    /** How many issues the dry run judged so far. */
    processed: number;
    /** How many it goes through, counted at the start; null for an older run. */
    total?: number | null;
    /** The requeue mode the owners and actions of the report are said in. */
    requeueMode?: string | null;
    /** 'newest' for newest issue first; empty for an older run in row key order. */
    order?: string | null;
    /** The size of the batch; null for a run over every issue of the scope. */
    limit?: number | null;
    /** Whether there is more after this batch. */
    more?: boolean;
    /** The run this batch goes on from. */
    continuesRun?: string | null;
    /** The batch that goes on from this run, once started. */
    nextBatch?: string | null;
    /** Until when the run waits for Exact's new day. */
    pausedUntil?: string | null;
    report?: ReevaluationReport | null;
    apply?: ReevaluationApply | null;
}

/** An earlier re-evaluation in short. Mirrors ReevaluationSummaryDto. */
export interface ReevaluationSummary {
    runId: string;
    createdBy?: string | null;
    createdAt: string;
    scope: string;
    state: ReevaluationState;
    toClose: number;
    toReopen: number;
    toReclassify: number;
    closed: number;
}

export interface ReevaluationStart {
    customer?: string;
    environment?: string;
    stage?: Stage | '';
    /** The most issues the run judges, newest first; empty for every issue. */
    limit?: number;
}

// The generated client's axios instance hands the body back as text.
const body = <T>(data: unknown): T =>
    (typeof data === 'string' && data.length > 0
        ? JSON.parse(data)
        : data) as T;

export const getReevaluations = async (
    axios: AxiosInstance,
    signal?: AbortSignal
): Promise<ReevaluationSummary[]> =>
    body<ReevaluationSummary[]>(
        (await axios.get('/Issues/reevaluation', { signal })).data
    ) ?? [];

export const getReevaluation = async (
    axios: AxiosInstance,
    runId: string,
    signal?: AbortSignal
): Promise<ReevaluationRun> =>
    body<ReevaluationRun>(
        (
            await axios.get(
                `/Issues/reevaluation/${encodeURIComponent(runId)}`,
                { signal }
            )
        ).data
    );

/** Starts a dry run and does its first step; reads only. */
export const startReevaluation = async (
    axios: AxiosInstance,
    start: ReevaluationStart
): Promise<ReevaluationRun> =>
    body<ReevaluationRun>(
        (await axios.post('/Issues/reevaluation', start)).data
    );

/** The next step of a dry run, where the last one stopped; reads only. */
export const continueReevaluation = async (
    axios: AxiosInstance,
    runId: string
): Promise<ReevaluationRun> =>
    body<ReevaluationRun>(
        (
            await axios.post(
                `/Issues/reevaluation/${encodeURIComponent(runId)}/continue`
            )
        ).data
    );

/** Starts the next batch of a run whose batch was full, from where it stopped; reads only. */
export const nextReevaluationBatch = async (
    axios: AxiosInstance,
    runId: string
): Promise<ReevaluationRun> =>
    body<ReevaluationRun>(
        (
            await axios.post(
                `/Issues/reevaluation/${encodeURIComponent(runId)}/next-batch`
            )
        ).data
    );

/**
 * The run with every decision it made, as the text the API wrote: what "Kopieer rapport (JSON)" puts on the
 * clipboard, to paste into a chat.
 */
export const exportReevaluation = async (
    axios: AxiosInstance,
    runId: string
): Promise<string> => {
    const response = await axios.get(
        `/Issues/reevaluation/${encodeURIComponent(runId)}/export`,
        { headers: { Accept: 'application/json' }, responseType: 'text' }
    );

    // Indented, so it reads in a chat; the API sends it on one line.
    return JSON.stringify(body<unknown>(response.data), null, 2);
};

/** Applies a finished dry run, a part at a time: call again until `apply.complete`. */
export const applyReevaluation = async (
    axios: AxiosInstance,
    runId: string
): Promise<ReevaluationRun> =>
    body<ReevaluationRun>(
        (
            await axios.post(
                `/Issues/reevaluation/${encodeURIComponent(runId)}/apply`
            )
        ).data
    );

/** Whether a deploy of one environment is followed by a dry run of the backlog. Mirrors ReevaluateOptInDto. */
export interface ReevaluateOptIn {
    customer: string;
    environment: string;
    /** The environment's own choice; null when it has none and Rollout:Reevaluate decides. */
    enabled?: boolean | null;
    /** Whether the rollout watch starts the dry run. */
    effective: boolean;
    changedBy?: string | null;
    changedAt?: string | null;
}

/** Every environment's choice, and Rollout:Reevaluate for the others. Mirrors ReevaluateOptInsDto. */
export interface ReevaluateOptIns {
    configured: boolean;
    environments: ReevaluateOptIn[];
}

const afterDeployPath = (customer: string, environment: string) =>
    `/Issues/reevaluation/after-deploy/${encodeURIComponent(customer)}/${encodeURIComponent(environment)}`;

export const getReevaluateOptIns = async (
    axios: AxiosInstance,
    signal?: AbortSignal
): Promise<ReevaluateOptIns> =>
    body<ReevaluateOptIns>(
        (await axios.get('/Issues/reevaluation/after-deploy', { signal })).data
    ) ?? { configured: false, environments: [] };

export const getReevaluateOptIn = async (
    axios: AxiosInstance,
    customer: string,
    environment: string
): Promise<ReevaluateOptIn> =>
    body<ReevaluateOptIn>((await axios.get(afterDeployPath(customer, environment))).data);

export const saveReevaluateOptIn = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    enabled: boolean
): Promise<ReevaluateOptIn> =>
    body<ReevaluateOptIn>((await axios.put(afterDeployPath(customer, environment), { enabled })).data);
